import { getFootprintSquares, minimumFootprintDistanceFeet, normalizeCreatureSize, tokenAnchorCell } from './pushGeometry';
import { weaponReachFeet } from './weaponReach';

export const CLEAVE_DAMAGE_POLICY = 'omit-positive';

const combatantId = (participant) => String(participant?.characterId || participant?.enemyId || participant?.combatantId || participant?.id || '');

// Map rendering obtains size from its combatant metadata lookup, while persisted
// map tokens intentionally contain positioning data only. Keep Cleave on that
// same source instead of silently treating an unadorned token as Medium.
export const getCleaveCreatureSize = ({ id, participant, token, entities } = {}) =>
  normalizeCreatureSize(entities?.[String(id)]?.size) ||
  normalizeCreatureSize(participant?.size ?? participant?.creatureSize) ||
  normalizeCreatureSize(token?.size);

export const getCleaveCandidateIds = ({ attackerId, primaryTargetId, weapon, combatState, spatialContext } = {}) => {
  const map = spatialContext?.map;
  const tokens = spatialContext?.tokens;
  const sourceToken = tokens?.[attackerId];
  const primaryToken = tokens?.[primaryTargetId];
  if (!map || !sourceToken || !primaryToken) return [];
  const participants = Array.isArray(combatState?.participants) ? combatState.participants : [];
  const byId = new Map(participants.map((entry) => [combatantId(entry), entry]));
  const size = (id, token) => getCleaveCreatureSize({ id, participant: byId.get(String(id)), token, entities: spatialContext?.entities });
  return participants.map(combatantId).filter((candidateId) => {
    if (!candidateId || candidateId === String(attackerId) || candidateId === String(primaryTargetId)) return false;
    const participant = byId.get(candidateId);
    if (participant?.defeated === true || Number(participant?.currentHp) === 0) return false;
    const candidateToken = tokens[candidateId];
    if (!candidateToken) return false;
    const primarySize = size(primaryTargetId, primaryToken);
    const candidateSize = size(candidateId, candidateToken);
    const attackerSize = size(attackerId, sourceToken);
    const adjacent = minimumFootprintDistanceFeet({ sourceToken: primaryToken, sourceSize: primarySize, targetToken: candidateToken, targetSize: candidateSize, map });
    const inReach = minimumFootprintDistanceFeet({ sourceToken, sourceSize: attackerSize, targetToken: candidateToken, targetSize: candidateSize, map });
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Cleave] candidate geometry', {
        attackerId: String(attackerId), primaryTargetId: String(primaryTargetId), candidateId,
        sizes: { attacker: attackerSize, primary: primarySize, candidate: candidateSize },
        footprints: { attacker: getFootprintSquares(attackerSize), primary: getFootprintSquares(primarySize), candidate: getFootprintSquares(candidateSize) },
        anchors: { attacker: tokenAnchorCell(sourceToken, map), primary: tokenAnchorCell(primaryToken, map), candidate: tokenAnchorCell(candidateToken, map) },
        distances: { primaryToCandidate: adjacent, attackerToCandidate: inReach }, reach: weaponReachFeet(weapon),
      });
    }
    return adjacent != null && adjacent <= 5 && inReach != null && inReach <= weaponReachFeet(weapon);
  });
};

export const qualifiesForCleave = ({ attack, outcome, combatState, attackerId, candidateIds } = {}) => {
  const ledger = combatState?.turnActionLedger;
  return attack?.kind === 'weapon' && attack?.isMeleeAttack === true &&
    ['hit', 'critical-hit'].includes(outcome) && attack?.hasWeaponMastery === true &&
    String(attack?.mastery?.label || attack?.weapon?.mastery || '').toLowerCase() === 'cleave' &&
    !(ledger?.combatantId === String(attackerId) && ledger?.turnSequence === combatState?.turnSequence && ledger?.cleave?.spent) &&
    Array.isArray(candidateIds) && candidateIds.length > 0;
};

export const createCleaveDecision = (resolutionId) => ({
  id: `${resolutionId}:weapon-mastery-cleave`, featureId: 'weapon-mastery-cleave', resolutionId,
  label: 'Weapon Mastery: Cleave', prompt: 'Make a Cleave attack against another nearby creature?',
  choices: [{ id: 'apply', label: 'Cleave' }, { id: 'skip', label: 'Skip' }],
});
